import { z } from "zod";
import { defineEndpoint, idParamsSchema } from "./endpoint.js";
import { paisa } from "./money.js";
/**
 * Inventory (docs/specs/inventory.md). Quantities are integers in the unit's smallest step (grams of a kg
 * product), sent as decimal strings because they are `bigint` on both ends and may exceed 2⁵³ in principle.
 */
export const stockQtySchema = z.string().regex(/^-?\d{1,16}$/, "A whole number in the unit's smallest step");
/** I-01 ledger entry types. */
export const STOCK_ENTRY_TYPES = [
    "opening",
    "purchase_receipt",
    "purchase_return",
    "sale",
    "sale_return",
    "transfer_out",
    "transfer_in",
    "adjustment",
];
export const stockEntryTypeSchema = z.enum(STOCK_ENTRY_TYPES);
/** Adjustment reasons: I-01's five plus `found` and `other` (owner decision 2026-10-26). */
export const ADJUSTMENT_REASONS = [
    "damage",
    "loss",
    "theft",
    "expiry",
    "count_correction",
    "found",
    "other",
];
export const adjustmentReasonSchema = z.enum(ADJUSTMENT_REASONS);
/** One variant × location after a call to the inventory's single stock-changing use case. */
export const stockChangedEventSchema = z.object({
    variantId: z.uuid(),
    locationId: z.uuid(),
    onHand: stockQtySchema,
    reserved: stockQtySchema,
    available: stockQtySchema,
    avgCostPaisa: paisa(),
    deltas: z.array(z.object({ ledgerEntryId: z.uuid(), type: stockEntryTypeSchema, qtyDelta: stockQtySchema })),
    sourceType: z.string(),
    sourceId: z.uuid(),
});
/** A variant × location went from zero or more to below zero (I-05). */
export const stockNegativeEventSchema = z.object({
    variantId: z.uuid(),
    locationId: z.uuid(),
    onHand: stockQtySchema,
    sourceType: z.string(),
    sourceId: z.uuid(),
});
// Reads ----------------------------------------------------------------------------------------------------------
/** What the Admin shows about the variant beside its stock (from the catalog). */
export const stockVariantSchema = z.object({
    productId: z.uuid(),
    productName: z.string(),
    sku: z.string(),
    optionValues: z.record(z.string(), z.string()),
    unitCode: z.string(),
    /** Quantities are in 10^-precision of the unit (kg with precision 3 → grams). */
    unitPrecision: z.int(),
    status: z.enum(["active", "archived"]),
});
export const stockLevelSchema = z.object({
    id: z.uuid(),
    variantId: z.uuid(),
    locationId: z.uuid(),
    onHand: stockQtySchema,
    reserved: stockQtySchema,
    available: stockQtySchema,
    /** Dispatched to this location, not received yet (B-26); in no location's on hand or available. */
    inTransit: stockQtySchema,
    avgCostPaisa: paisa(),
    lastMovementAt: z.iso.datetime().nullable(),
    variant: stockVariantSchema,
});
export const STOCK_STATES = ["negative", "zero", "positive"];
export const stockListQuerySchema = z.object({
    locationId: z.uuid().optional(),
    productId: z.uuid().optional(),
    /** Comma-separated variant IDs (e.g. from a search), at most 100. */
    variantIds: z
        .string()
        .transform((value) => value.split(",").filter((id) => id !== ""))
        .pipe(z.array(z.uuid()).max(100))
        .optional(),
    state: z.enum(STOCK_STATES).optional(),
    limit: z.coerce.number().int().min(1).max(100).default(50),
    cursor: z.uuid().optional(),
});
export const stockListSchema = z.object({ data: z.array(stockLevelSchema), nextCursor: z.uuid().nullable() });
/** One variant at every location the caller may see, with the total over them. */
export const variantStockSchema = z.object({
    variantId: z.uuid(),
    variant: stockVariantSchema,
    total: z.object({
        onHand: stockQtySchema,
        reserved: stockQtySchema,
        available: stockQtySchema,
        inTransit: stockQtySchema,
    }),
    locations: z.array(stockLevelSchema.omit({ variant: true })),
});
export const stockEntrySchema = z.object({
    id: z.uuid(),
    variantId: z.uuid(),
    locationId: z.uuid(),
    type: stockEntryTypeSchema,
    reason: adjustmentReasonSchema.nullable(),
    qtyDelta: stockQtySchema,
    unitCostPaisa: paisa(),
    sourceType: z.string(),
    sourceId: z.uuid(),
    occurredAt: z.iso.datetime(),
    note: z.string(),
    createdBy: z.uuid().nullable(),
    createdAt: z.iso.datetime(),
});
/**
 * I-10: entries in business-time order with the balance after each; `openingBalance` is the stock at `from`.
 * `from` is inclusive and `to` exclusive, so a day is [its start, the next day's start).
 */
export const stockHistoryQuerySchema = z.object({
    variantId: z.uuid(),
    locationId: z.uuid(),
    // With an offset too: the Admin sends a day's start in the tenant's zone (`2026-10-01T00:00:00+06:00`).
    from: z.iso.datetime({ offset: true }).optional(),
    to: z.iso.datetime({ offset: true }).optional(),
    limit: z.coerce.number().int().min(1).max(100).default(50),
    cursor: z.uuid().optional(),
});
export const stockHistorySchema = z.object({
    openingBalance: stockQtySchema,
    data: z.array(stockEntrySchema.extend({ runningBalance: stockQtySchema })),
    nextCursor: z.uuid().nullable(),
});
// Adjustments and opening stock ---------------------------------------------------------------------------------
export const MAX_ADJUSTMENT_LINES = 200;
const note = z.string().trim().max(500).default("");
const distinctVariants = (lines) => new Set(lines.map((line) => line.variantId.toLowerCase())).size === lines.length;
/** `POST /v1/stock-adjustments`: signed quantities; the reason must fit the sign (docs/specs/inventory.md). */
export const createStockAdjustmentRequestSchema = z
    .object({
    locationId: z.uuid(),
    reason: adjustmentReasonSchema,
    note,
    lines: z
        .array(z
        .object({
        variantId: z.uuid(),
        qtyDelta: stockQtySchema.refine((value) => BigInt(value) !== 0n, "Cannot be zero"),
        /** Increases only: the cost per unit that goes into the average (default: the current average). */
        unitCostPaisa: paisa().min(0).optional(),
    })
        .strict())
        .min(1)
        .max(MAX_ADJUSTMENT_LINES)
        .refine(distinctVariants, "Each variant once"),
})
    .strict();
/** `POST /v1/opening-stock`: first stock of variants at a location that has none yet. */
export const postOpeningStockRequestSchema = z
    .object({
    locationId: z.uuid(),
    note,
    lines: z
        .array(z
        .object({
        variantId: z.uuid(),
        qty: stockQtySchema.refine((value) => BigInt(value) > 0n, "Must be more than zero"),
        /** Default: the variant's catalog cost. */
        unitCostPaisa: paisa().min(0).optional(),
    })
        .strict())
        .min(1)
        .max(MAX_ADJUSTMENT_LINES)
        .refine(distinctVariants, "Each variant once"),
})
    .strict();
export const stockAdjustmentSchema = z.object({
    id: z.uuid(),
    locationId: z.uuid(),
    reason: z.union([adjustmentReasonSchema, z.literal("opening")]),
    note: z.string(),
    lineCount: z.int(),
    createdBy: z.uuid().nullable(),
    createdAt: z.iso.datetime(),
});
export const stockAdjustmentDetailSchema = stockAdjustmentSchema.extend({
    lines: z.array(stockEntrySchema.extend({ variant: stockVariantSchema })),
});
export const stockAdjustmentListQuerySchema = z.object({
    locationId: z.uuid().optional(),
    reason: z.union([adjustmentReasonSchema, z.literal("opening")]).optional(),
    limit: z.coerce.number().int().min(1).max(100).default(50),
    cursor: z.uuid().optional(),
});
export const stockAdjustmentListSchema = z.object({
    data: z.array(stockAdjustmentSchema),
    nextCursor: z.uuid().nullable(),
});
// Settings (C-07) -------------------------------------------------------------------------------------------------
/** Defaults for new products; each product's own flags are what apply. Version 0 until first saved. */
export const inventorySettingsSchema = z.object({
    defaultTrackInventory: z.boolean(),
    defaultAllowNegative: z.boolean(),
    version: z.int(),
});
export const updateInventorySettingsRequestSchema = z
    .object({
    defaultTrackInventory: z.boolean().optional(),
    defaultAllowNegative: z.boolean().optional(),
    version: z.int().min(0),
})
    .strict();
// Endpoints -------------------------------------------------------------------------------------------------------
const inventory = { tag: "inventory" };
const view = { permission: "inventory.view" };
const adjust = { permission: "inventory.adjust" };
export const listStock = defineEndpoint({
    ...inventory,
    name: "listStock",
    method: "GET",
    path: "/v1/stock",
    access: view,
    summary: "Stock per variant and location (only the caller's locations)",
    query: stockListQuerySchema,
    status: 200,
    response: stockListSchema,
});
export const getVariantStock = defineEndpoint({
    ...inventory,
    name: "getVariantStock",
    method: "GET",
    path: "/v1/variants/{id}/stock",
    access: view,
    summary: "One variant's stock at every location the caller may see",
    params: idParamsSchema,
    status: 200,
    response: variantStockSchema,
});
export const getStockHistory = defineEndpoint({
    ...inventory,
    name: "getStockHistory",
    method: "GET",
    path: "/v1/stock/history",
    access: view,
    summary: "Ledger entries of a variant at a location with running balance",
    query: stockHistoryQuerySchema,
    status: 200,
    response: stockHistorySchema,
});
export const listStockAdjustments = defineEndpoint({
    ...inventory,
    name: "listStockAdjustments",
    method: "GET",
    path: "/v1/stock-adjustments",
    access: view,
    summary: "Adjustments and opening stock entries, newest first",
    query: stockAdjustmentListQuerySchema,
    status: 200,
    response: stockAdjustmentListSchema,
});
export const getStockAdjustment = defineEndpoint({
    ...inventory,
    name: "getStockAdjustment",
    method: "GET",
    path: "/v1/stock-adjustments/{id}",
    access: view,
    summary: "One adjustment with its lines",
    params: idParamsSchema,
    status: 200,
    response: stockAdjustmentDetailSchema,
});
export const createStockAdjustment = defineEndpoint({
    ...inventory,
    name: "createStockAdjustment",
    method: "POST",
    path: "/v1/stock-adjustments",
    access: adjust,
    summary: "Adjust stock at a location with a reason (audited)",
    body: createStockAdjustmentRequestSchema,
    status: 201,
    response: stockAdjustmentDetailSchema,
    errors: ["INSUFFICIENT_STOCK", "STOCK_NOT_TRACKED", "LOCATION_INACTIVE"],
    idempotent: true,
});
export const postOpeningStock = defineEndpoint({
    ...inventory,
    name: "postOpeningStock",
    method: "POST",
    path: "/v1/opening-stock",
    access: adjust,
    summary: "Enter the first stock of variants at a location (audited)",
    body: postOpeningStockRequestSchema,
    status: 201,
    response: stockAdjustmentDetailSchema,
    errors: ["OPENING_STOCK_EXISTS", "STOCK_NOT_TRACKED", "LOCATION_INACTIVE"],
    idempotent: true,
});
export const getInventorySettings = defineEndpoint({
    ...inventory,
    name: "getInventorySettings",
    method: "GET",
    path: "/v1/inventory/settings",
    access: { permission: "settings.view" },
    summary: "Stock defaults for new products",
    status: 200,
    response: inventorySettingsSchema,
});
export const updateInventorySettings = defineEndpoint({
    ...inventory,
    name: "updateInventorySettings",
    method: "PATCH",
    path: "/v1/inventory/settings",
    access: { permission: "settings.manage" },
    summary: "Change the stock defaults for new products (audited)",
    body: updateInventorySettingsRequestSchema,
    status: 200,
    response: inventorySettingsSchema,
    errors: ["CONFLICT"],
});
//# sourceMappingURL=inventory.js.map